/* Run & Tell: Turkey Trot team name and costume generator.
   Reads its lists from [data-rt-tt-data] inside each [data-rt-tt] root.
   Picker rule (content.json): names match who (or "any") and vibe (or "any");
   costumes and shirt lines match who (or "any"). Shuffle never repeats the item on screen.
   Team card: canvas PNG in the brand font pair (Sofia Sans Extra Condensed 900 + Gaegu 700),
   with runntell.com always drawn on it. Save uses the native share sheet with the PNG file on
   touch devices (same approach as the sign maker), otherwise a download. */
(function () {
  'use strict';

  var CARD_W = 1080, CARD_H = 1350;
  var CREDIT = 'runntell.com';
  var FONT_TIMEOUT = 4000;
  var COLORS = { bg: '#0F0BE3', ink: '#FFF9EC', sun: '#FFE135' };
  var FONTS = {
    name: { family: '"Sofia Sans Extra Condensed", "Arial Narrow", "Arial Black", sans-serif', weight: '900' },
    hand: { family: 'Gaegu, "Marker Felt", "Comic Sans MS", cursive', weight: '700' }
  };

  function parseJSON(s) { try { return JSON.parse(s); } catch (e) { return null; } }
  function fontStr(f, size) { return f.weight + ' ' + Math.max(1, Math.round(size)) + 'px ' + f.family; }
  function withTimeout(p, ms) { return Promise.race([p, new Promise(function (r) { setTimeout(r, ms); })]); }
  function slug(s) {
    return (s || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[’']/g, '')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'team';
  }

  /* ---------- picking ---------- */
  function matches(item, key, val) { var v = item && item[key]; return v === val || v === 'any'; }
  function pool(list, who, vibe) {
    return (list || []).filter(function (x) {
      return x && x.t && matches(x, 'who', who) && (vibe == null || !('vibe' in x) || matches(x, 'vibe', vibe));
    });
  }
  function pick(list, avoid) {
    if (!list.length) return null;
    var choices = list.length > 1 ? list.filter(function (x) { return x.t !== avoid; }) : list;
    if (!choices.length) choices = list;
    return choices[Math.floor(Math.random() * choices.length)];
  }

  /* ---------- canvas helpers ---------- */
  function wrap(ctx, text, maxW) {
    var words = text.split(/\s+/), lines = [], cur = '';
    for (var i = 0; i < words.length; i++) {
      var test = cur ? cur + ' ' + words[i] : words[i];
      if (cur && ctx.measureText(test).width > maxW) { lines.push(cur); cur = words[i]; } else { cur = test; }
    }
    if (cur) lines.push(cur);
    return lines;
  }
  /* largest size where the wrapped text fits the box (and no single word overflows) */
  function fitText(ctx, text, font, w, h, maxSize, minSize, lh, maxLines) {
    for (var size = maxSize; size >= minSize; size -= 2) {
      ctx.font = fontStr(font, size);
      var lines = wrap(ctx, text, w);
      var widest = Math.max.apply(null, lines.map(function (l) { return ctx.measureText(l).width; }));
      if (lines.length <= maxLines && widest <= w && lines.length * size * lh <= h) return { size: size, lines: lines };
    }
    ctx.font = fontStr(font, minSize);
    return { size: minSize, lines: wrap(ctx, text, w) };
  }
  function drawLines(ctx, fit, font, cx, top, h, lh) {
    ctx.font = fontStr(font, fit.size);
    var total = fit.lines.length * fit.size * lh;
    var y = top + (h - total) / 2 + fit.size * lh / 2;
    fit.lines.forEach(function (l) { ctx.fillText(l, cx, y); y += fit.size * lh; });
  }

  function drawCard(ctx, W, H, data) {
    var s = W / CARD_W;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(s, s);
    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, CARD_W, CARD_H);
    /* frame */
    ctx.strokeStyle = COLORS.ink;
    ctx.lineWidth = 4;
    ctx.strokeRect(40, 40, CARD_W - 80, CARD_H - 80);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    /* kicker */
    ctx.fillStyle = COLORS.sun;
    ctx.font = fontStr(FONTS.hand, 64);
    ctx.fillText(data.kicker || '', CARD_W / 2, 150);
    /* squiggle under the kicker */
    ctx.strokeStyle = COLORS.sun;
    ctx.lineWidth = 6;
    ctx.lineCap = 'round';
    ctx.beginPath();
    for (var x = 340; x <= 740; x += 10) {
      var y = 205 + Math.sin((x - 340) / 22) * 7;
      if (x === 340) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
    /* team name */
    ctx.fillStyle = COLORS.ink;
    var nameFit = fitText(ctx, data.name || '', FONTS.name, 880, 640, 230, 70, 0.98, 4);
    drawLines(ctx, nameFit, FONTS.name, CARD_W / 2, 250, 640, 0.98);
    /* shirt line */
    if (data.shirt) {
      ctx.fillStyle = COLORS.sun;
      var shirtFit = fitText(ctx, data.shirt, FONTS.hand, 840, 220, 76, 36, 1.15, 3);
      drawLines(ctx, shirtFit, FONTS.hand, CARD_W / 2, 920, 220, 1.15);
    }
    /* credit: always drawn */
    ctx.fillStyle = COLORS.ink;
    ctx.font = fontStr(FONTS.hand, 54);
    ctx.fillText(CREDIT, CARD_W / 2, 1235);
    ctx.restore();
  }

  function canvasToBlob(c) {
    return new Promise(function (resolve, reject) {
      try { c.toBlob(function (b) { b ? resolve(b) : reject(new Error('empty')); }, 'image/png'); } catch (e) { reject(e); }
    });
  }

  /* ---------- one instance ---------- */
  function init(root) {
    if (!root || root.__rtTt) return;
    root.__rtTt = true;
    var dataEl = root.querySelector('[data-rt-tt-data]');
    var data = parseJSON(dataEl ? dataEl.textContent : '') || {};
    var $ = function (sel) { return root.querySelector(sel); };
    var form = $('[data-rt-tt-form]');
    var result = $('[data-rt-tt-result]');
    var card = $('[data-rt-tt-card]');
    var elName = $('[data-rt-tt-name]');
    var elCostume = $('[data-rt-tt-costume]');
    var elShirt = $('[data-rt-tt-shirt]');
    var btnShuffle = $('[data-rt-tt-shuffle]');
    var btnSave = $('[data-rt-tt-save]');
    var status = $('[data-rt-tt-status]');
    var live = $('[data-rt-tt-live]');
    var canvas = $('[data-rt-tt-canvas]');
    if (!form || !result || !elName) return;

    var current = { name: null, costume: null, shirt: null };
    var pending = null; /* { file } ready for the share sheet, so the tap goes straight to navigator.share */
    var fontsReady = false;

    function choice(group) {
      var r = root.querySelector('input[data-rt-tt-' + group + ']:checked');
      return r ? r.value : null;
    }
    function say(msg) { if (status) status.textContent = msg || ''; }
    function mark(k, v) { root.setAttribute('data-rt-tt-' + k, v); } /* QA hook */

    function roll() {
      var who = choice('who'), vibe = choice('vibe');
      if (!who || !vibe) return false;
      var names = pool(data.names, who, vibe);
      if (!names.length) names = pool(data.names, who, null); /* safety net, never empty */
      var n = pick(names, current.name && current.name.t);
      var c = pick(pool(data.costumes, who, null), current.costume && current.costume.t);
      var s = pick(pool(data.shirt_lines, who, null), current.shirt && current.shirt.t);
      current = { name: n, costume: c, shirt: s };
      elName.textContent = n ? n.t : '';
      if (elCostume) elCostume.textContent = c ? c.t : '';
      if (elShirt) elShirt.textContent = s ? s.t : '';
      mark('combo', who + '/' + vibe);
      if (live) live.textContent = (n ? n.t + '. ' : '') + (c ? c.t + ' ' : '') + (s ? s.t : '');
      say('');
      drawPreview();
      return true;
    }

    function cardData() {
      return { name: current.name ? current.name.t : '', shirt: current.shirt ? current.shirt.t : '', kicker: root.getAttribute('data-rt-tt-kicker') || '' };
    }
    function fileName() { return 'turkey-trot-team-' + slug(cardData().name) + '.png'; }

    function drawPreview() {
      pending = null;
      if (!canvas) return;
      if (canvas.width !== CARD_W) { canvas.width = CARD_W; canvas.height = CARD_H; }
      var d = cardData();
      drawCard(canvas.getContext('2d'), CARD_W, CARD_H, d);
      canvas.setAttribute('aria-label', 'Team card preview: ' + d.name + (d.shirt ? '. ' + d.shirt : '') + '. ' + CREDIT);
      /* prepare the PNG ahead of the tap (iOS only opens the share sheet straight from a tap) */
      if (!fontsReady) return;
      var key = d.name + '|' + d.shirt;
      canvasToBlob(canvas).then(function (blob) {
        if (key !== cardData().name + '|' + cardData().shirt) return;
        try { pending = { key: key, file: new File([blob], fileName(), { type: 'image/png' }), blob: blob }; }
        catch (e) { pending = { key: key, file: null, blob: blob }; }
        root.setAttribute('data-rt-tt-png', String(blob.size));
      }).catch(function () {});
    }

    function isTouch() { return (window.matchMedia && matchMedia('(pointer:coarse)').matches) || /iPad|iPhone|iPod|Android/.test(navigator.userAgent); }
    function canShareFile(f) { try { return !!(f && navigator.canShare && navigator.share && navigator.canShare({ files: [f] })); } catch (e) { return false; } }

    function download(blob) {
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url; a.download = fileName();
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(function () { URL.revokeObjectURL(url); }, 60000);
      mark('save-path', 'download');
      say('Saved. Your team card is a ' + CARD_W + ' x ' + CARD_H + ' PNG.');
    }

    function getBlob() {
      if (pending && pending.blob) return Promise.resolve(pending);
      var c = document.createElement('canvas');
      c.width = CARD_W; c.height = CARD_H;
      drawCard(c.getContext('2d'), CARD_W, CARD_H, cardData());
      return canvasToBlob(c).then(function (blob) {
        var f = null;
        try { f = new File([blob], fileName(), { type: 'image/png' }); } catch (e) {}
        return { file: f, blob: blob };
      });
    }

    function onSave() {
      if (!current.name) return;
      var ready = pending;
      if (ready && ready.file && isTouch() && canShareFile(ready.file)) {
        mark('save-path', 'share');
        navigator.share({ files: [ready.file], title: cardData().name })
          .then(function () { say('Shared. Send it to the group chat.'); })
          .catch(function (err) {
            if (err && err.name === 'AbortError') { say(''); return; }
            download(ready.blob);
          });
        return;
      }
      btnSave.disabled = true;
      getBlob().then(function (out) { download(out.blob); })
        .catch(function (e) { say('Sorry, the card could not be made on this device. Try another browser.'); if (window.console) console.error('[rt-turkey-trot-names]', e); })
        .then(function () { btnSave.disabled = false; });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!roll()) return;
      var first = result.hidden;
      result.hidden = false;
      if (first && card) {
        card.focus({ preventScroll: true });
        if (card.scrollIntoView) card.scrollIntoView({ block: 'nearest' });
      }
    });
    Array.prototype.forEach.call(root.querySelectorAll('input[data-rt-tt-who],input[data-rt-tt-vibe]'), function (r) {
      r.addEventListener('change', function () { if (!result.hidden) roll(); });
    });
    if (btnShuffle) btnShuffle.addEventListener('click', roll);
    if (btnSave) btnSave.addEventListener('click', onSave);

    /* brand fonts: wait (max 4 s), then redraw so the card uses them */
    var loads = (document.fonts && document.fonts.load) ? [FONTS.name, FONTS.hand].map(function (f) {
      return document.fonts.load(fontStr(f, 48), 'Gobble Till You Wobble runntell.com 3.1').catch(function () {});
    }) : [];
    withTimeout(Promise.all(loads), FONT_TIMEOUT).then(function () {
      fontsReady = true;
      root.setAttribute('data-rt-tt-ready', '1');
      if (current.name) drawPreview();
    });
  }

  function boot() { Array.prototype.forEach.call(document.querySelectorAll('[data-rt-tt]'), init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
  window.RtTurkeyTrot = { pool: pool, pick: pick, drawCard: drawCard, CARD_W: CARD_W, CARD_H: CARD_H };
})();
